import React from "react";
import { createPortal } from "react-dom";
import i18next from "i18next";
import { FiChevronDown, FiChevronUp, FiCornerUpLeft, FiCornerUpRight, FiGrid } from "react-icons/fi";
import { cellFillStyle, normalizeCounter } from "components/StaffingLevels/staffingNeedGrid";
import {
	applyRestrictionUpdates,
	cloneRestrictionConfig,
	readRestriction,
	restrictionPeak,
	restrictionSignature,
} from "./restrictionGrid";

const HISTORY_LIMIT = 40;
const ASSIGN_DIGITS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

class EmployeeDayTypeGrid extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			brush: null,
			customBrush: "",
			clipboard: null,
			menu: null,
			editing: null,
			history: [],
			future: [],
			overlay: null,
		};
		this.gridRef = React.createRef();
		this.editInput = null;
		this.painting = false;
		this.paintHistoryRecorded = false;
		this.paintValue = 0;
		this.paintAnchor = null;
		this.paintBase = [];
		this.skipEditCommit = false;
	}

	componentDidMount() {
		document.addEventListener("mousedown", this.onDocumentMouseDown, true);
	}

	componentWillUnmount() {
		document.removeEventListener("mousedown", this.onDocumentMouseDown, true);
	}

	displayConfig = () => this.state.overlay || this.props.values || [];

	armDigit = (value) => {
		this.setState((prev) => {
			const armed = prev.brush === value && prev.customBrush === "";
			return {
				brush: armed ? null : value,
				customBrush: "",
				editing: null,
				menu: null,
			};
		});
	};

	onCustomBrush = (event) => {
		const raw = event.target.value.replace(/[^\d]/g, "").slice(0, 3);
		this.setState({
			customBrush: raw,
			brush: raw === "" ? null : normalizeCounter(raw),
			editing: null,
			menu: null,
		});
	};

	onGridPointerDown = (event) => {
		if (event.button !== 0 || this.state.brush == null) return;
		const cell = event.target.closest && event.target.closest("[data-dtv-cell]");
		if (!cell || !this.gridRef.current || !this.gridRef.current.contains(cell)) return;
		event.preventDefault();
		this.painting = true;
		this.paintHistoryRecorded = false;
		this.paintValue = this.state.brush;
		this.paintAnchor = cell;
		this.paintBase = cloneRestrictionConfig(this.props.values);
		if (event.currentTarget.setPointerCapture) {
			event.currentTarget.setPointerCapture(event.pointerId);
		}
		this.applyPaint(this.cellsInRectangle(cell, cell));
	};

	onGridPointerMove = (event) => {
		if (!this.painting || !this.paintAnchor) return;
		const el = document.elementFromPoint(event.clientX, event.clientY);
		const cell = el && el.closest ? el.closest("[data-dtv-cell]") : null;
		if (!cell || !this.gridRef.current || !this.gridRef.current.contains(cell)) return;
		this.applyPaint(this.cellsInRectangle(this.paintAnchor, cell));
	};

	onGridPointerUp = () => {
		this.painting = false;
		this.paintAnchor = null;
		if (this.state.overlay) this.setState({ overlay: null });
	};

	cellsInRectangle = (anchor, current) => {
		const root = this.gridRef.current;
		if (!root) return [];
		const cells = Array.from(root.querySelectorAll("[data-dtv-cell]"));
		const rowIndex = new Map();
		const colIndex = new Map();
		cells.forEach((cell) => {
			if (!rowIndex.has(cell.dataset.rowId)) rowIndex.set(cell.dataset.rowId, rowIndex.size);
			if (!colIndex.has(cell.dataset.dayId)) colIndex.set(cell.dataset.dayId, colIndex.size);
		});
		const anchorRow = rowIndex.get(anchor.dataset.rowId);
		const currentRow = rowIndex.get(current.dataset.rowId);
		const anchorCol = colIndex.get(anchor.dataset.dayId);
		const currentCol = colIndex.get(current.dataset.dayId);
		if (anchorRow == null || currentRow == null || anchorCol == null || currentCol == null) return [];
		const minRow = Math.min(anchorRow, currentRow);
		const maxRow = Math.max(anchorRow, currentRow);
		const minCol = Math.min(anchorCol, currentCol);
		const maxCol = Math.max(anchorCol, currentCol);

		return cells
			.filter((cell) => {
				const row = rowIndex.get(cell.dataset.rowId);
				const col = colIndex.get(cell.dataset.dayId);
				return row >= minRow && row <= maxRow && col >= minCol && col <= maxCol;
			})
			.map((cell) => ({
				rowId: cell.dataset.rowId,
				dayTypeId: Number(cell.dataset.dayId),
			}));
	};

	publish = (next, historyPatch) => {
		this.props.onChange(next);
		this.setState((prev) => ({
			overlay: next,
			editing: null,
			menu: null,
			...historyPatch(prev),
		}));
	};

	applyPaint = (cells) => {
		if (!cells.length) return;
		const updates = cells.map((cell) => ({
			field: cell.rowId,
			dayTypeId: cell.dayTypeId,
			value: this.paintValue,
		}));
		const next = applyRestrictionUpdates(this.paintBase, updates);
		const changed = restrictionSignature(next) !== restrictionSignature(this.paintBase);
		if (!changed) {
			if (this.paintHistoryRecorded) {
				this.paintHistoryRecorded = false;
				this.props.onChange(cloneRestrictionConfig(this.paintBase));
				this.setState((prev) => ({
					overlay: null,
					history: prev.history.slice(0, -1),
				}));
			}
			return;
		}
		const record = !this.paintHistoryRecorded;
		this.paintHistoryRecorded = true;
		this.publish(next, (prev) => ({
			history: record
				? [...prev.history, cloneRestrictionConfig(this.paintBase)].slice(-HISTORY_LIMIT)
				: prev.history,
			future: record ? [] : prev.future,
		}));
	};

	setEditInput = (node) => {
		this.editInput = node;
	};

	openEdit = (cell) => {
		if (this.state.brush != null) return;
		if (this.state.editing && this.state.editing.key === cell.key) return;
		this.setState(
			{
				editing: {
					...cell,
					draft: cell.value > 0 ? String(cell.value) : "",
				},
				menu: null,
			},
			() => {
				const input = this.editInput;
				if (!input || document.activeElement !== input) return;
				input.select();
			}
		);
	};

	onEditDraft = (event) => {
		const draft = event.target.value.replace(/[^\d]/g, "").slice(0, 3);
		this.setState((prev) => (prev.editing ? { editing: { ...prev.editing, draft } } : null));
	};

	commitEdit = () => {
		const input = this.editInput;
		const editing = this.state.editing;
		if (!editing) return;
		const { rowId, dayTypeId, draft } = editing;
		const value = normalizeCounter(draft);
		const current = readRestriction(this.props.values, rowId, dayTypeId);
		const previous = cloneRestrictionConfig(this.props.values);
		const changed = current !== value;
		if (changed) {
			this.props.onChange(applyRestrictionUpdates(this.props.values, [{
				field: rowId,
				dayTypeId,
				value,
			}]));
		}
		this.setState((prev) => ({
			editing: null,
			overlay: null,
			...(changed
				? {
					history: [...prev.history, previous].slice(-HISTORY_LIMIT),
					future: [],
				}
				: {}),
		}), () => {
			if (input && document.activeElement === input) {
				this.skipEditCommit = true;
				input.blur();
			}
		});
	};

	onEditBlur = (event) => {
		if (this.skipEditCommit) {
			this.skipEditCommit = false;
			return;
		}
		const next = event.relatedTarget;
		if (next && next.closest && next.closest("[data-dtv-stepper]")) return;
		this.commitEdit();
	};

	onEditKeyDown = (event) => {
		if (event.key === "Enter") {
			event.preventDefault();
			this.commitEdit();
		}
		if (event.key === "Escape") {
			event.preventDefault();
			event.stopPropagation();
			const input = event.target;
			this.skipEditCommit = true;
			this.setState({ editing: null }, () => {
				if (input && document.activeElement === input) input.blur();
			});
		}
	};

	stepCell = (delta) => {
		const { editing } = this.state;
		if (!editing) return;
		const current = readRestriction(this.props.values, editing.rowId, editing.dayTypeId);
		const value = Math.min(999, Math.max(0, normalizeCounter(editing.draft) + delta));
		const nextDraft = value > 0 ? String(value) : "";
		if (value === current && editing.draft === nextDraft) return;
		const next = value === current
			? null
			: applyRestrictionUpdates(this.props.values, [{
				field: editing.rowId,
				dayTypeId: editing.dayTypeId,
				value,
			}]);
		if (next) this.props.onChange(next);
		this.setState((prev) => ({
			editing: prev.editing ? { ...prev.editing, draft: nextDraft, value } : null,
			menu: null,
			...(next
				? {
					history: [...prev.history, cloneRestrictionConfig(this.props.values)].slice(-HISTORY_LIMIT),
					future: [],
				}
				: {}),
		}));
	};

	undo = () => {
		const { history, future } = this.state;
		if (!history.length || this.painting) return;
		const previous = history[history.length - 1];
		this.props.onChange(cloneRestrictionConfig(previous));
		this.setState({
			history: history.slice(0, -1),
			future: [...future, cloneRestrictionConfig(this.props.values)],
			editing: null,
			menu: null,
			overlay: null,
		});
	};

	redo = () => {
		const { history, future } = this.state;
		if (!future.length || this.painting) return;
		const next = future[future.length - 1];
		this.props.onChange(cloneRestrictionConfig(next));
		this.setState({
			history: [...history, cloneRestrictionConfig(this.props.values)].slice(-HISTORY_LIMIT),
			future: future.slice(0, -1),
			editing: null,
			menu: null,
			overlay: null,
		});
	};

	onKeyDown = (event) => {
		const key = event.key.toLowerCase();
		const meta = event.metaKey || event.ctrlKey;
		if (!meta || (key !== "z" && key !== "y")) return;
		const tag = event.target.tagName;
		if (tag === "INPUT" || tag === "TEXTAREA") return;
		event.preventDefault();
		if (key === "y" || event.shiftKey) this.redo();
		else this.undo();
	};

	openMenu = (event, spec) => {
		event.preventDefault();
		event.stopPropagation();
		const rect = event.currentTarget.getBoundingClientRect();
		const left = Math.max(8, Math.min(rect.left, window.innerWidth - 188));
		this.setState((prev) => {
			if (prev.menu && prev.menu.id === spec.id) return { menu: null };
			return {
				menu: {
					...spec,
					top: rect.bottom + 6,
					left,
				},
			};
		});
	};

	copyColumn = (dayTypeId) => {
		const config = this.displayConfig();
		const values = {};
		this.props.rows.forEach((row) => {
			values[row.id] = readRestriction(config, row.id, dayTypeId);
		});
		this.setState({ clipboard: { kind: "column", values }, menu: null });
	};

	pasteColumn = (dayTypeId) => {
		const { clipboard } = this.state;
		if (!clipboard || clipboard.kind !== "column") {
			this.setState({ menu: null });
			return;
		}
		const updates = this.props.rows.map((row) => ({
			field: row.id,
			dayTypeId,
			value: clipboard.values[row.id] || 0,
		}));
		this.commitBatch(applyRestrictionUpdates(this.props.values, updates));
	};

	copyRow = (rowId) => {
		const config = this.displayConfig();
		const values = {};
		this.props.columns.forEach((column) => {
			values[column.id] = readRestriction(config, rowId, column.id);
		});
		this.setState({ clipboard: { kind: "row", values }, menu: null });
	};

	pasteRow = (rowId) => {
		const { clipboard } = this.state;
		if (!clipboard || clipboard.kind !== "row") {
			this.setState({ menu: null });
			return;
		}
		const updates = this.props.columns.map((column) => ({
			field: rowId,
			dayTypeId: column.id,
			value: clipboard.values[column.id] || 0,
		}));
		this.commitBatch(applyRestrictionUpdates(this.props.values, updates));
	};

	commitBatch = (next) => {
		if (restrictionSignature(next) === restrictionSignature(this.props.values)) {
			this.setState({ menu: null, editing: null });
			return;
		}
		this.props.onChange(next);
		this.setState((prev) => ({
			menu: null,
			editing: null,
			overlay: null,
			history: [...prev.history, cloneRestrictionConfig(this.props.values)].slice(-HISTORY_LIMIT),
			future: [],
		}));
	};

	onDocumentMouseDown = (event) => {
		const { menu, editing } = this.state;
		if (!menu && !editing) return;
		const target = event.target;
		const closest = target.closest ? (selector) => target.closest(selector) : () => null;
		if (menu && !closest("[data-dtv-menu]") && !closest("[data-dtv-menu-trigger]")) {
			this.setState({ menu: null });
		}
		if (editing && !closest("[data-dtv-stepper]")) {
			this.skipEditCommit = true;
			this.commitEdit();
		}
	};

	renderCell = (row, column, peak) => {
		const { brush, editing } = this.state;
		const value = readRestriction(this.displayConfig(), row.id, column.id);
		const key = `${row.id}:${column.id}`;
		const isEditing = editing && editing.key === key;
		const fillStyle = cellFillStyle(value, peak);
		const shown = value > 0 ? String(value) : "-";
		const label = `${row.label}, ${column.name}: ${shown}`;
		const cell = { key, rowId: row.id, dayTypeId: column.id, value };

		if (brush != null) {
			return (
				<td key={column.id}>
					<button
						type="button"
						className={`sl-cell is-paintable${fillStyle ? " is-filled" : ""}`}
						style={fillStyle || undefined}
						data-dtv-cell="true"
						data-row-id={row.id}
						data-day-id={column.id}
						aria-label={label}
					>
						{shown}
					</button>
				</td>
			);
		}

		return (
			<td key={column.id}>
				<div
					className={`sl-cell-editor${isEditing ? " is-editing" : ""}${value > 0 ? " is-filled" : ""}`}
					style={fillStyle || undefined}
					data-dtv-stepper={isEditing ? "true" : undefined}
				>
					<input
						ref={isEditing ? this.setEditInput : undefined}
						type="text"
						inputMode="numeric"
						size={1}
						autoComplete="off"
						spellCheck={false}
						className="sl-stepper-value"
						value={isEditing ? editing.draft : value > 0 ? String(value) : ""}
						placeholder="-"
						aria-label={label}
						onFocus={() => this.openEdit(cell)}
						onChange={this.onEditDraft}
						onBlur={this.onEditBlur}
						onKeyDown={this.onEditKeyDown}
					/>
					{isEditing && (
						<span className="sl-stepper-arrows">
							<button
								type="button"
								aria-label={label}
								onMouseDown={(event) => event.preventDefault()}
								onClick={() => this.stepCell(1)}
							>
								<FiChevronUp aria-hidden="true" />
							</button>
							<button
								type="button"
								aria-label={label}
								onMouseDown={(event) => event.preventDefault()}
								onClick={() => this.stepCell(-1)}
							>
								<FiChevronDown aria-hidden="true" />
							</button>
						</span>
					)}
				</div>
			</td>
		);
	};

	renderMenu = () => {
		const { menu, clipboard } = this.state;
		if (!menu) return null;
		const isColumn = menu.type === "column";
		const canPaste = clipboard && clipboard.kind === (isColumn ? "column" : "row");

		return createPortal(
			<div className="sl-menu" style={{ top: menu.top, left: menu.left }} data-dtv-menu="true" role="menu">
				<button
					type="button"
					role="menuitem"
					onClick={() => (isColumn ? this.copyColumn(menu.dayTypeId) : this.copyRow(menu.rowId))}
				>
					{i18next.t(isColumn ? "staffingLevels.form.copyColumn" : "staffingLevels.form.copyRow")}
				</button>
				<button
					type="button"
					role="menuitem"
					disabled={!canPaste}
					onClick={() => (isColumn ? this.pasteColumn(menu.dayTypeId) : this.pasteRow(menu.rowId))}
				>
					{i18next.t(isColumn ? "staffingLevels.form.pasteColumn" : "staffingLevels.form.pasteRow")}
				</button>
			</div>,
			document.body
		);
	};

	renderAssign = () => {
		const { brush, customBrush, history, future } = this.state;
		return (
			<>
				<div className="sl-assign">
					<span className="sl-assign-label">{i18next.t("staffingLevels.form.assign")}:</span>
					<div className="sl-assign-digits">
						{ASSIGN_DIGITS.map((digit) => (
							<button
								key={digit}
								type="button"
								className={`sl-digit${brush === digit && customBrush === "" ? " is-armed" : ""}`}
								onClick={() => this.armDigit(digit)}
							>
								{digit}
							</button>
						))}
						<input
							type="text"
							inputMode="numeric"
							className={`sl-digit sl-digit-custom${customBrush !== "" ? " is-armed" : ""}`}
							value={customBrush}
							placeholder={i18next.t("staffingLevels.form.customValue")}
							aria-label={i18next.t("staffingLevels.form.customValue")}
							onChange={this.onCustomBrush}
							onKeyDown={(event) => {
								if (event.key === "Enter") event.preventDefault();
							}}
						/>
					</div>
					<div className="sl-history">
						<button
							type="button"
							className="sl-history-btn"
							onClick={this.undo}
							disabled={!history.length}
							aria-label={i18next.t("staffingLevels.form.undo")}
							title={i18next.t("staffingLevels.form.undo")}
						>
							<FiCornerUpLeft />
						</button>
						<button
							type="button"
							className="sl-history-btn"
							onClick={this.redo}
							disabled={!future.length}
							aria-label={i18next.t("staffingLevels.form.redo")}
							title={i18next.t("staffingLevels.form.redo")}
						>
							<FiCornerUpRight />
						</button>
					</div>
				</div>
				{brush != null && (
					<div className="sl-assign-banner">
						<FiGrid aria-hidden="true" />
						<span>{i18next.t("staffingLevels.form.assignMode", { value: brush })}</span>
					</div>
				)}
			</>
		);
	};

	render() {
		const { columns, rows } = this.props;
		const { menu, brush } = this.state;
		if (!columns || !columns.length || !rows || !rows.length) return null;
		const peak = restrictionPeak(this.displayConfig());

		return (
			<div className="staffing-level-grid employee-day-type-grid" onKeyDown={this.onKeyDown}>
				{this.renderAssign()}
				<div
					className={`sl-grid-scroll${brush != null ? " is-painting" : ""}`}
					ref={this.gridRef}
					onPointerDown={this.onGridPointerDown}
					onPointerMove={this.onGridPointerMove}
					onPointerUp={this.onGridPointerUp}
					onPointerCancel={this.onGridPointerUp}
				>
					<table className="sl-grid">
						<thead>
							<tr>
								<th className="sl-corner" />
								{columns.map((column) => {
									const menuId = `col-${column.id}`;
									return (
										<th key={column.id}>
											<button
												type="button"
												className={`sl-head-pill${menu && menu.id === menuId ? " is-open" : ""}`}
												data-dtv-menu-trigger="true"
												onClick={(event) => this.openMenu(event, {
													id: menuId,
													type: "column",
													dayTypeId: column.id,
												})}
												onContextMenu={(event) => this.openMenu(event, {
													id: menuId,
													type: "column",
													dayTypeId: column.id,
												})}
											>
												{column.name}
											</button>
										</th>
									);
								})}
							</tr>
						</thead>
						<tbody>
							{rows.map((row) => {
								const menuId = `row-${row.id}`;
								return (
									<tr key={row.id}>
										<th className="sl-shift-head">
											<button
												type="button"
												className={`sl-row-label${menu && menu.id === menuId ? " is-open" : ""}`}
												data-dtv-menu-trigger="true"
												onClick={(event) => this.openMenu(event, {
													id: menuId,
													type: "row",
													rowId: row.id,
												})}
												onContextMenu={(event) => this.openMenu(event, {
													id: menuId,
													type: "row",
													rowId: row.id,
												})}
											>
												{row.label}
											</button>
										</th>
										{columns.map((column) => this.renderCell(row, column, peak))}
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
				{this.renderMenu()}
			</div>
		);
	}
}

export default EmployeeDayTypeGrid;
